import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEduAuth } from '../context/EduAuthContext';
import { Mail, Lock, ArrowRight, RefreshCw, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/funnel`;

export default function FunnelPage() {
  const { ref: refParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { eduUser, eduLogin } = useEduAuth();

  // Attribution ref comes from route param or query string
  const ref = refParam || searchParams.get('ref') || '';

  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'success'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // If already authenticated, go straight to edu
  useEffect(() => {
    if (eduUser) navigate('/edu', { replace: true });
  }, [eduUser, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/capture`, { email: email.trim(), ref });
      setStep('otp');
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/verify`, {
        email: email.trim(),
        otp: otp.trim(),
      });
      setStep('success');
      setTimeout(() => {
        eduLogin(data.token, data.email);
        // Redirect to edu subdomain, passing token for cross-domain handoff
        const eduUrl = process.env.REACT_APP_EDU_URL || `${window.location.protocol}//edu.${window.location.hostname.replace(/^www\./, '')}`;
        window.location.href = `${eduUrl}?token=${data.token}`;
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/resend`, { email: email.trim(), ref });
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend. Please wait and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col items-center justify-center p-4">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-pink-600/8 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500/20 to-purple-600/20 border border-purple-500/20 rounded-full px-4 py-1.5 mb-6">
            <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Free Access</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
            Get instant access to<br />
            <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              exclusive content
            </span>
          </h1>
          <p className="text-gray-400 text-sm">
            Enter your email to receive your free access to the Ruonn education library.
          </p>
        </div>

        {/* Card */}
        <div className="bg-[#111115] border border-white/8 rounded-2xl p-7 shadow-2xl">

          {/* Step: Email */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="funnel-email-form">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    data-testid="funnel-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {error && (
                <p data-testid="funnel-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                data-testid="funnel-email-submit"
                type="submit"
                disabled={loading || !email}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>Send My Access Code <ArrowRight className="w-4 h-4" /></>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                No password needed. We'll email you a one-time code.
              </p>
            </form>
          )}

          {/* Step: OTP */}
          {step === 'otp' && (
            <div data-testid="funnel-otp-step">
              <div className="flex items-center gap-2 mb-5">
                <button
                  onClick={() => { setStep('email'); setOtp(''); setError(''); }}
                  className="text-gray-500 hover:text-gray-300 text-xs transition-colors"
                >
                  ← Back
                </button>
                <p className="text-sm text-gray-400 flex-1 text-center">
                  Code sent to <span className="text-white font-medium">{email}</span>
                </p>
              </div>

              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">Verification Code</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      data-testid="funnel-otp-input"
                      type="text"
                      required
                      maxLength={6}
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-lg text-white text-center tracking-widest font-mono placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5 text-center">
                    6-digit code from your email · expires in 15 minutes
                  </p>
                </div>

                {error && (
                  <p data-testid="funnel-otp-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}

                <button
                  data-testid="funnel-otp-submit"
                  type="submit"
                  disabled={loading || otp.length < 6}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Verify & Get Access'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || loading}
                    className="text-xs text-gray-400 hover:text-purple-400 transition-colors disabled:opacity-50"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't receive it? Resend code"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div data-testid="funnel-success" className="text-center py-6 space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">You're in!</h3>
                <p className="text-sm text-gray-400">Redirecting you to your courses...</p>
              </div>
              <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-4">
          Powered by Ruonn &nbsp;·&nbsp; No spam, ever
        </p>
      </div>
    </div>
  );
}
